import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { ageOn, isPaused, pausedLast, pluralYears } from '../data/athletes'
import {
  useAthletes,
  useClubContext,
  useGroup,
  useGroupAthletes,
  useGroupJournal,
  useGroupPayments,
  useGroupScheduleTimeline,
  useGroups,
  usePaymentMarks,
  useSessions,
} from '../data/hooks'
import {
  addDays,
  formatDayLong,
  formatDayShort,
  formatMonth,
  formatTimeRange,
  isoDate,
  parseIsoDate,
  pluralAthletes,
} from '../data/schedule'
import type { AttendanceStatus, GroupDetail, GroupSchedule, SessionSlot } from '../data/types'
import { ChooseEventSheet } from './EventSheets'
import { DeleteGroupSheet } from './GroupForms'
import { useBackTo } from './AthleteScreen'
import { NotesSection } from './NotesScreens'
import { useOnline } from '../platform/network'
import type { MonthlyPaymentStatus } from '@shared/types/monthly_payment'

const WEEKDAYS = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота']

/** Месяц YYYY-MM: из адреса или текущий. */
function monthParam(raw: string | null, now: Date): string {
  if (raw && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) return raw
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthDate(ym: string): Date {
  const [y, m] = ym.split('-').map(Number)
  return new Date(y, m - 1, 1)
}

function MonthSwitcher({ ym, onChange, label }: { ym: string; onChange: (ym: string) => void; label: string }) {
  return (
    <div className="pc-week-nav" role="group" aria-label={label}>
      <button type="button" className="pc-icon-btn" onClick={() => onChange(shiftMonth(ym, -1))} aria-label="Предыдущий месяц">
        <ChevronLeft size={22} aria-hidden="true" />
      </button>
      <h2 aria-live="polite">{formatMonth(monthDate(ym))}</h2>
      <button type="button" className="pc-icon-btn" onClick={() => onChange(shiftMonth(ym, 1))} aria-label="Следующий месяц">
        <ChevronRight size={22} aria-hidden="true" />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Список групп
// ---------------------------------------------------------------------------

export function GroupsScreen() {
  const navigate = useNavigate()
  const { now } = useEnv()
  const groups = useGroups()
  const athletes = useAthletes()
  const today = now()
  const sessions = useSessions(isoDate(today), isoDate(addDays(today, 13)))

  const counts = useMemo(() => {
    const m = new Map<string, number>()
    // Кто на паузе, в размер группы не входит
    for (const a of athletes.data ?? []) if (a.group_id && !isPaused(a)) m.set(a.group_id, (m.get(a.group_id) ?? 0) + 1)
    return m
  }, [athletes.data])

  const nextOf = (groupId: string) => (sessions.data ?? []).find((s) => s.groupId === groupId && !s.cancelled)

  let body
  if (groups.isPending) body = <Skeleton rows={3} label="Загружаю группы" />
  else if (groups.isError) body = <ErrorState title="Не удалось загрузить группы" onRetry={() => void groups.refetch()} />
  else if (groups.data.length === 0)
    body = (
      <EmptyState
        title="Групп пока нет"
        text="Группа - это состав и расписание занятий. Создайте первую: название и дни можно поменять потом."
        action={
          <button type="button" className="pc-btn pc-btn--primary" onClick={() => navigate('/groups/new')}>
            Создать группу
          </button>
        }
      />
    )
  else
    body = (
      <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
        {groups.data.map((g) => {
          const next = nextOf(g.id)
          const nextDate = next ? parseIsoDate(next.date) : null
          return (
            <li key={g.id}>
              <button type="button" className="pc-list-row" onClick={() => navigate(`/groups/${g.id}`)}>
                <span className="pc-list-row__main">
                  <span className="pc-list-row__title" style={{ display: 'block' }}>
                    {g.name}
                  </span>
                  <span className="pc-list-row__meta">
                    {athletes.data ? pluralAthletes(counts.get(g.id) ?? 0) : ' '}
                    {next && nextDate ? ` / ближайшее ${formatDayShort(nextDate)}, ${next.startTime}` : ''}
                  </span>
                </span>
                <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
              </button>
            </li>
          )
        })}
      </ul>
    )

  // Пустой список уже ведёт к созданию: вторая кнопка внизу не нужна.
  const showAdd = !groups.isPending && !(groups.data && groups.data.length === 0)
  return (
    <Screen
      title="Группы"
      actionBar={
        showAdd ? (
          <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => navigate('/groups/new')}>
            Новая группа
          </button>
        ) : undefined
      }
    >
      {body}
    </Screen>
  )
}

// ---------------------------------------------------------------------------
// Группа (S06)
// ---------------------------------------------------------------------------

type Tab = 'roster' | 'schedule' | 'journal' | 'payments' | 'notes'
const TABS: [Tab, string][] = [
  ['roster', 'Состав'],
  ['schedule', 'Расписание'],
  ['journal', 'Журнал'],
  ['payments', 'Оплаты'],
  // Записи группы (этап 5 шаг 3, SC17)
  ['notes', 'Записи'],
]

/**
 * S06 «Группа». Название и направление в шапке на всех вкладках (в старом
 * интерфейсе название терялось на журнале и оплатах). Вкладка и месяц в адресе.
 */
export function GroupScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [menu, setMenu] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const back = useBackTo('/groups')
  const group = useGroup(id)
  const roster = useGroupAthletes(id)
  const club = useClubContext()
  const { now } = useEnv()

  const tab: Tab = (['roster', 'schedule', 'journal', 'payments', 'notes'] as const).find((t) => t === params.get('tab')) ?? 'roster'
  const ym = monthParam(params.get('m'), now())
  const setParam = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    setParams(p, { replace: true })
  }

  if (group.isPending) {
    return (
      <Screen title={' '} back={back}>
        <Skeleton rows={3} label="Загружаю группу" />
      </Screen>
    )
  }
  if (group.isError) {
    const notFound = (group.error as { kind?: string })?.kind === 'not_found'
    return (
      <Screen title="Группа" back={back}>
        {notFound ? (
          <EmptyState title="Группа не найдена" text="Возможно, её удалили или ссылка устарела." />
        ) : (
          <ErrorState title="Не удалось открыть группу" onRetry={() => void group.refetch()} />
        )}
      </Screen>
    )
  }

  const g = group.data
  const ages = (roster.data ?? [])
    .filter((a) => !isPaused(a))
    .map((a) => ageOn(a.date_of_birth, now()))
    .filter((x): x is number => x !== null)
  const ageRange =
    ages.length > 0
      ? Math.min(...ages) === Math.max(...ages)
        ? pluralYears(ages[0])
        : `${Math.min(...ages)}-${pluralYears(Math.max(...ages))}`
      : null
  const subtitle = [
    club.data?.directionName,
    ageRange,
    g.coaches.length > 0 ? `Тренер: ${g.coaches.map((c) => c.name).join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' / ')

  return (
    <Screen
      title={g.name}
      subtitle={subtitle}
      back={back}
      hubContext={{ label: `группа ${g.name}`, title: g.name, detail: 'Группа', ref: { kind: 'group', groupId: g.id } }}
      headerAction={
        <button type="button" className="pc-icon-btn" aria-label="Действия с группой" onClick={() => setMenu(true)}>
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
      }
    >
      {g.description && (
        <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14, whiteSpace: 'pre-line' }}>
          {g.description}
        </p>
      )}
      <div className="pc-segments pc-segments--wrap" role="tablist" aria-label="Разделы группы" style={{ marginTop: 16 }}>
        {TABS.map(([t, label]) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setParam({ tab: t === 'roster' ? null : t })}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'roster' && <RosterTab group={g} />}
      {tab === 'schedule' && <ScheduleTab group={g} />}
      {tab === 'journal' && <JournalTab groupId={g.id} ym={ym} onMonth={(m) => setParam({ m })} />}
      {tab === 'payments' && <PaymentsTab groupId={g.id} ym={ym} onMonth={(m) => setParam({ m })} />}
      {tab === 'notes' && <NotesSection owner={{ kind: 'group', id: g.id }} />}

      {menu && (
        <Sheet title={g.name} onClose={() => setMenu(false)}>
          <button
            type="button"
            className="pc-btn pc-btn--block"
            onClick={() => {
              setMenu(false)
              navigate(`/groups/${g.id}/edit`)
            }}
          >
            Название, описание, тренеры
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--block"
            onClick={() => {
              setMenu(false)
              // После сохранения - на вкладку «Расписание»: видно, что получилось.
              navigate(`/groups/${g.id}/schedule`)
            }}
          >
            Изменить расписание
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            onClick={() => {
              setMenu(false)
              setConfirmDelete(true)
            }}
          >
            Удалить группу
          </button>
        </Sheet>
      )}
      {confirmDelete && <DeleteGroupSheet group={g} onClose={() => setConfirmDelete(false)} />}
    </Screen>
  )
}

function useUpcoming(groupId: string): { next: SessionSlot | undefined; list: SessionSlot[]; isError: boolean; refetch: () => void } {
  const { now } = useEnv()
  const today = now()
  const sessions = useSessions(isoDate(today), isoDate(addDays(today, 13)))
  const nowKey = `${isoDate(today)} ${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}`
  const list = (sessions.data ?? []).filter((s) => s.groupId === groupId)
  const next = list.find((s) => !s.cancelled && `${s.date} ${s.endTime || s.startTime}` >= nowKey)
  return { next, list, isError: sessions.isError, refetch: () => void sessions.refetch() }
}

function RosterTab({ group: g }: { group: GroupDetail }) {
  const navigate = useNavigate()
  const { now } = useEnv()
  const roster = useGroupAthletes(g.id)
  const pausedCount = (roster.data ?? []).filter(isPaused).length
  const upcoming = useUpcoming(g.id)
  const [pickEvent, setPickEvent] = useState(false)
  const nextDate = upcoming.next ? parseIsoDate(upcoming.next.date) : null

  return (
    <>
      <section className="pc-card" style={{ marginTop: 16 }} aria-label="Ближайшее занятие">
        <div className="pc-muted" style={{ fontSize: 14 }}>
          Ближайшее занятие
        </div>
        {upcoming.next && nextDate ? (
          <button
            type="button"
            className="pc-list-row"
            style={{ minHeight: 0, padding: '4px 0 0' }}
            onClick={() => navigate(`/sessions/${encodeURIComponent(upcoming.next!.key)}`)}
          >
            <span className="pc-list-row__main" style={{ fontSize: 20, fontWeight: 700 }}>
              {isoDate(nextDate) === isoDate(now()) ? 'Сегодня' : formatDayLong(nextDate)}, {formatTimeRange(upcoming.next)}
            </span>
            <ChevronRight size={20} aria-hidden="true" className="pc-muted" />
          </button>
        ) : (
          <div style={{ marginTop: 4, fontWeight: 600 }}>
            {upcoming.isError ? 'Не удалось загрузить расписание' : 'В ближайшие две недели занятий нет'}
          </div>
        )}
      </section>

      <section className="pc-section" aria-labelledby="pc-roster-title">
        <h2 id="pc-roster-title" className="pc-section-title">
          Спортсмены{roster.data ? ` / ${roster.data.length - pausedCount}` : ''}
          {pausedCount > 0 ? `, на паузе ${pausedCount}` : ''}
        </h2>
        {roster.isPending ? (
          <Skeleton rows={3} label="Загружаю состав" />
        ) : roster.isError ? (
          <ErrorState title="Не удалось загрузить состав" onRetry={() => void roster.refetch()} />
        ) : roster.data.length === 0 ? (
          <EmptyState title="В группе пока нет спортсменов" />
        ) : (
          <ul className="pc-list pc-card" style={{ padding: '0 12px' }}>
            {pausedLast(roster.data).map((a) => {
              const age = ageOn(a.date_of_birth, now())
              const paused = isPaused(a)
              const ageText = age !== null ? pluralYears(age) : 'Возраст не указан'
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    className="pc-list-row"
                    data-paused={paused || undefined}
                    onClick={() => navigate(`/athletes/${a.id}`)}
                  >
                    <span className="pc-list-row__main">
                      <span className="pc-list-row__title" style={{ display: 'block' }}>
                        {a.name}
                      </span>
                      <span className="pc-list-row__meta">{paused ? `На паузе / ${ageText}` : ageText}</span>
                    </span>
                    <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}
        <button
          type="button"
          className="pc-btn pc-btn--block"
          style={{ marginTop: 12 }}
          onClick={() => navigate(`/athletes/new?g=${g.id}`)}
        >
          Добавить в группу
        </button>
        <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} onClick={() => setPickEvent(true)}>
          Записать группу на событие
        </button>
      </section>

      {pickEvent && (
        <ChooseEventSheet
          title="Записать группу на событие"
          text={`Дальше откроется список: группа «${g.name}» уже отмечена, лишних можно снять.`}
          onChoose={(e) => navigate(`/schedule/events/${e.id}/add?g=${g.id}`)}
          onClose={() => setPickEvent(false)}
        />
      )}
    </>
  )
}

/** Понедельник первым, внутри дня по началу. */
function weekOrder(slots: GroupSchedule[]): GroupSchedule[] {
  return [...slots].sort((a, b) => ((a.day_of_week + 6) % 7) - ((b.day_of_week + 6) % 7) || a.start_time.localeCompare(b.start_time))
}

function SlotRows({ slots, label }: { slots: GroupSchedule[]; label?: string }) {
  return (
    <ul className="pc-list pc-card" style={{ padding: '0 12px' }} aria-label={label}>
      {weekOrder(slots).map((s, i) => (
        <li key={i}>
          <div className="pc-kv">
            <div className="pc-kv__label">{WEEKDAYS[s.day_of_week] ?? '?'}</div>
            <div className="pc-kv__value">
              {formatTimeRange({ startTime: s.start_time, endTime: s.end_time })}
              {s.location ? `, ${s.location}` : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

/** «С 2 ноября» */
function fromLabel(iso: string): string {
  const d = parseIsoDate(iso)
  return `С ${d ? formatDayShort(d) : iso}`
}

function ScheduleTab({ group: g }: { group: GroupDetail }) {
  const navigate = useNavigate()
  const { now } = useEnv()
  const upcoming = useUpcoming(g.id)
  const timeline = useGroupScheduleTimeline(g.id, isoDate(now()))
  const slots = weekOrder(g.schedule)
  const planned = timeline.data?.planned ?? []

  return (
    <>
      <section className="pc-section" aria-labelledby="pc-week-slots">
        <h2 id="pc-week-slots" className="pc-section-title">
          По неделям
        </h2>
        {planned.length > 0 && (
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Действует сейчас
          </p>
        )}
        {slots.length === 0 ? (
          <EmptyState title="Расписание не задано" text="Без расписания занятия группы не появятся в календаре." />
        ) : (
          <SlotRows slots={slots} label="Действует сейчас" />
        )}

        {timeline.isPending ? (
          <Skeleton rows={1} label="Проверяю запланированные изменения" />
        ) : timeline.isError ? (
          <div style={{ marginTop: 12 }}>
            <ErrorState
              title="Не удалось проверить запланированные изменения"
              text="Расписание на будущие даты могли уже задать."
              onRetry={() => void timeline.refetch()}
            />
          </div>
        ) : (
          planned.map((v) => (
            <section key={v.id} aria-label={`Запланировано: ${fromLabel(v.validFrom).toLowerCase()}`} style={{ marginTop: 16 }}>
              <h3 className="pc-section-title" style={{ fontSize: 16, margin: '0 0 8px' }}>
                {fromLabel(v.validFrom)}
              </h3>
              {v.schedule.length === 0 ? (
                <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
                  Занятий по расписанию не будет.
                </p>
              ) : (
                <SlotRows slots={v.schedule} />
              )}
            </section>
          ))
        )}

        <button
          type="button"
          className="pc-btn pc-btn--block"
          style={{ marginTop: 12, justifyContent: 'center' }}
          onClick={() => navigate(`/groups/${g.id}/schedule`, { state: { fromGroupTab: true } })}
        >
          {slots.length === 0 && planned.length === 0 ? 'Задать расписание' : 'Изменить расписание'}
        </button>
      </section>

      <section className="pc-section" aria-labelledby="pc-next-sessions">
        <h2 id="pc-next-sessions" className="pc-section-title">
          Ближайшие две недели
        </h2>
        {upcoming.isError ? (
          <ErrorState title="Не удалось загрузить занятия" onRetry={upcoming.refetch} />
        ) : upcoming.list.length === 0 ? (
          <EmptyState title="Занятий нет" />
        ) : (
          upcoming.list.map((s) => {
            const d = parseIsoDate(s.date)
            // Отменённое тоже открывается: на экране занятия его можно вернуть.
            return (
              <button
                key={s.key}
                type="button"
                className="pc-slot"
                data-cancelled={s.cancelled || undefined}
                onClick={() => navigate(`/sessions/${encodeURIComponent(s.key)}`)}
              >
                <div className="pc-slot__time">{d ? formatDayLong(d) : s.date}</div>
                <div className="pc-slot__title" style={{ fontSize: 17 }}>
                  {formatTimeRange(s)}
                  {s.extra ? ', разовое' : ''}
                </div>
                {s.cancelled && (
                  <span className="pc-badge pc-badge--neutral" style={{ marginTop: 6 }}>
                    Отменено
                  </span>
                )}
              </button>
            )
          })
        )}
      </section>
    </>
  )
}

const MARK: Record<AttendanceStatus, { sym: string; label: string }> = {
  present: { sym: '+', label: 'есть' },
  absent: { sym: '-', label: 'нет' },
  late: { sym: 'О', label: 'опоздание' },
  left_early: { sym: 'Р', label: 'ранний уход' },
  sick: { sym: 'Б', label: 'болезнь' },
  excused: { sym: 'У', label: 'уважительная причина' },
}

function JournalTab({ groupId, ym, onMonth }: { groupId: string; ym: string; onMonth: (ym: string) => void }) {
  const first = monthDate(ym)
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
  const journal = useGroupJournal(groupId, isoDate(first), isoDate(last), true)

  return (
    <section style={{ marginTop: 8 }}>
      <MonthSwitcher ym={ym} onChange={onMonth} label="Месяц журнала" />
      <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14, textAlign: 'center' }}>
        Период: {formatDayShort(first)} - {formatDayShort(last)} {last.getFullYear()}
      </p>
      {journal.isPending ? (
        <Skeleton rows={3} label="Загружаю журнал" />
      ) : journal.isError ? (
        <ErrorState title="Не удалось загрузить журнал" onRetry={() => void journal.refetch()} />
      ) : journal.data.sessions.length === 0 ? (
        <EmptyState title="За этот месяц занятий не было" text="Отмеченные занятия появятся здесь." />
      ) : journal.data.rows.length === 0 ? (
        <EmptyState title="В группе нет спортсменов" />
      ) : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid var(--pc-border)', borderRadius: 12, background: 'var(--pc-surface)' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 14, minWidth: '100%' }}>
              <caption className="pc-visually-hidden">
                Журнал посещаемости за {formatMonth(first).toLowerCase()}
              </caption>
              <thead>
                <tr>
                  <th scope="col" style={{ position: 'sticky', left: 0, background: 'var(--pc-surface)', textAlign: 'left', padding: '8px 10px' }}>
                    Спортсмен
                  </th>
                  {journal.data.sessions.map((s) => {
                    const d = parseIsoDate(s.date.slice(0, 10))
                    return (
                      <th key={s.session_id} scope="col" style={{ padding: '8px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {d ? `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}` : s.date}
                        {s.cancelled ? ' отм.' : ''}
                      </th>
                    )
                  })}
                  <th scope="col" style={{ padding: '8px 10px' }}>
                    Итого
                  </th>
                </tr>
              </thead>
              <tbody>
                {journal.data.rows.map((r) => (
                  <tr key={r.athlete_id} style={{ borderTop: '1px solid var(--pc-border)' }}>
                    <th scope="row" style={{ position: 'sticky', left: 0, background: 'var(--pc-surface)', textAlign: 'left', padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r.name}
                    </th>
                    {journal.data.sessions.map((s) => {
                      const st = r.marks[s.session_id]
                      return (
                        <td key={s.session_id} style={{ textAlign: 'center', padding: '8px 6px' }} aria-label={st ? MARK[st].label : 'не отмечено'}>
                          {st ? MARK[st].sym : '·'}
                        </td>
                      )
                    })}
                    <td style={{ textAlign: 'center', padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      {r.present_count}/{journal.data.sessions.filter((s) => !s.cancelled).length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pc-muted" style={{ marginTop: 8, fontSize: 13 }}>
            + есть, - нет, О опоздание, Р ранний уход, Б болезнь, У уважительная причина, · не отмечено.
          </p>
        </>
      )}
    </section>
  )
}

function PaymentsTab({ groupId, ym, onMonth }: { groupId: string; ym: string; onMonth: (ym: string) => void }) {
  const payments = useGroupPayments(groupId, ym, true)
  const marks = usePaymentMarks(groupId, ym)
  const online = useOnline()

  const row = (p: MonthlyPaymentStatus, paused: boolean) => {
    const st = marks.states[p.athlete_id]
    const paidAt = p.paid_at ? parseIsoDate(p.paid_at.slice(0, 10)) : null
    let caption = p.is_paid ? `Отмечено${paidAt ? ` ${formatDayShort(paidAt)}` : ''}` : 'Нет отметки'
    if (paused) caption = p.is_paid ? `На паузе / ${caption}` : 'На паузе, оплату не ждём'
    if (st?.phase === 'saving') caption = 'Сохраняю…'
    if (st?.phase === 'error') caption = online ? 'Не сохранено' : 'Нет сети - не сохранено'
    return (
      <li key={p.athlete_id}>
        <div className="pc-roll-row" data-paused={paused || undefined}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pc-roll-row__name">{p.athlete_name}</div>
            <div className="pc-roll-row__state" data-tone={st?.phase === 'error' ? 'error' : undefined}>
              {caption}
            </div>
          </div>
          {st?.phase === 'error' ? (
            <button type="button" className="pc-btn" onClick={() => void marks.set(p.athlete_id, st.paid)}>
              Повторить
            </button>
          ) : p.is_paid ? (
            <button
              type="button"
              className="pc-btn"
              disabled={st?.phase === 'saving'}
              onClick={() => void marks.set(p.athlete_id, false)}
              aria-label={`Снять отметку оплаты: ${p.athlete_name}`}
            >
              Снять отметку
            </button>
          ) : (
            <button
              type="button"
              className={paused ? 'pc-btn' : 'pc-btn pc-btn--primary'}
              disabled={st?.phase === 'saving'}
              onClick={() => void marks.set(p.athlete_id, true)}
              aria-label={`Отметить оплату: ${p.athlete_name}`}
            >
              Отметить
            </button>
          )}
        </div>
      </li>
    )
  }

  const paused = payments.data?.paused ?? []

  return (
    <section style={{ marginTop: 8 }}>
      <MonthSwitcher ym={ym} onChange={onMonth} label="Месяц оплат" />
      {payments.isPending ? (
        <Skeleton rows={3} label="Загружаю оплаты" />
      ) : payments.isError ? (
        <ErrorState title="Не удалось загрузить оплаты" onRetry={() => void payments.refetch()} />
      ) : payments.data.athletes.length === 0 && paused.length === 0 ? (
        <EmptyState title="В группе нет спортсменов" />
      ) : (
        <>
          <h2 className="pc-section-title" style={{ marginTop: 4 }}>
            Оплата отмечена: {payments.data.paid_count} из {payments.data.total_count}
          </h2>
          {marks.failed > 0 && (
            <div className="pc-alert" role="alert">
              {online ? 'Не сохранено' : 'Нет сети - не сохранено'} отметок: {marks.failed}. Повторите в строке.
            </div>
          )}
          <ul className="pc-list" style={{ marginTop: 4 }}>
            {payments.data.athletes.map((p) => row(p, !!p.is_paused))}
          </ul>
          {paused.length > 0 && (
            <>
              <h2 className="pc-section-title" style={{ marginTop: 16 }}>
                На паузе / {paused.length}
              </h2>
              <p className="pc-muted" style={{ margin: '0 0 4px', fontSize: 14 }}>
                В отчёт об оплате не входят. Если всё же оплатили, отметьте - тогда попадут в список.
              </p>
              <ul className="pc-list">{paused.map((p) => row(p, true))}</ul>
            </>
          )}
          <div className="pc-note">
            Нет отметки - не значит, что платёж просрочен. Суммы и сроки здесь не считаются. «Снять отметку» только убирает
            отметку, деньги никуда не возвращаются.
          </div>
        </>
      )}
    </section>
  )
}

/** «Оплаты за месяц» с главной: оплаты ведутся по группам, сначала выбор группы. */
export function PaymentsPickScreen() {
  const navigate = useNavigate()
  const groups = useGroups()
  const back = useBackTo('/today')
  return (
    <Screen title="Оплаты" subtitle="Выберите группу" back={back}>
      {groups.isPending ? (
        <Skeleton rows={3} label="Загружаю группы" />
      ) : groups.isError ? (
        <ErrorState title="Не удалось загрузить группы" onRetry={() => void groups.refetch()} />
      ) : groups.data.length === 0 ? (
        <EmptyState
          title="Групп пока нет"
          text="Оплаты отмечаются по группам."
          action={
            <button type="button" className="pc-btn pc-btn--primary" onClick={() => navigate('/groups/new')}>
              Создать группу
            </button>
          }
        />
      ) : (
        <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 16 }}>
          {groups.data.map((g) => (
            <li key={g.id}>
              <button type="button" className="pc-list-row" onClick={() => navigate(`/groups/${g.id}?tab=payments`)}>
                <span className="pc-list-row__main pc-list-row__title">{g.name}</span>
                <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Screen>
  )
}
